import { FC, memo, useCallback, useState } from 'react'
import tw from 'tailwind-styled-components'
import { useTranslation } from 'next-i18next'
import { useRouterQuery } from 'next-router-query'
import { SvgIcon } from './SvgIcon'
import { Button } from './Button'
import { OnboardingModal } from './OnboardingModal'
import { AuctionPermissionIntent, useSettingsQuery } from '@foros-fe/graphql'
import Signature from '@foros-fe/assets/icons/signature.svg'
import ContactCard from '@foros-fe/assets/icons/participant-card.svg'
import Clock from '@foros-fe/assets/icons/clock-wait.svg'

type Props = {
  mode: 'bidder' | 'owner'
}

const Line = tw.div`flex`
const Column = tw(Line)`flex-col`
const Container = tw.div`
  flex
  flex-col
  rounded-lg
  bg-white
  p-6
  border
  border-gray-400
`
const Title = tw.p`
  text-lg
  font-semibold
`
const Text = tw.p`text-gray-800`
const Subtitle = tw.p`font-medium`
const Step = tw.div<{ $active?: boolean }>`
  w-[160px]
  md:w-[230px]
  h-2
  rounded-sm
  ${({ $active }) => ($active ? 'bg-green-500' : 'bg-gray-400')}
`

export const OnboardingCard: FC<Props> = memo(({ mode }) => {
  const { t } = useTranslation('onboarding')
  const query = useRouterQuery()
  const { data } = useSettingsQuery()
  const [modalOpen, setModalOpen] = useState(false)
  const closeModal = useCallback(() => setModalOpen(false), [setModalOpen])
  const openModal = useCallback(() => setModalOpen(true), [setModalOpen])

  const currentUser = data?.currentUser
  const owner = mode === 'owner'
  const agreementSent = owner
    ? currentUser?.auctionAgreementSent
    : currentUser?.biddingAgreementSent
  const id = query.id as string

  return currentUser ? (
    <Container>
      <Title>{owner ? t('auctionOwnerForm') : t('auctionBidderForm')}</Title>
      <Text className='mb-3'>{owner ? t('completeOwnerSteps') : t('completeBidderSteps')}</Text>
      <Line className='space-x-2 mb-7'>
        <Step $active={!!agreementSent} />
        <Step />
      </Line>
      <Line className='items-center mb-6'>
        <SvgIcon
          src={agreementSent ? Signature : ContactCard}
          width={52}
          height={40}
          className='mr-3'
        />
        <Column>
          <Subtitle>
            {agreementSent
              ? owner
                ? t('ownerContract')
                : t('bidderContract')
              : t('participantData')}
          </Subtitle>
          <Text>
            {agreementSent
              ? owner
                ? t('ownerContractSubtitle')
                : t('bidderContractSubtitle')
              : t('contactInfoRequired')}
          </Text>
        </Column>
      </Line>
      <Line className='items-center'>
        {agreementSent ? (
          <>
            <SvgIcon src={Clock} width={22} height={18} className='mr-2' />
            <Subtitle>{t('contractBeingPrepared')}</Subtitle>
          </>
        ) : (
          <Button value='open-onboarding-modal' title={t('enterData')} onClick={openModal} />
        )}
      </Line>
      <OnboardingModal
        {...currentUser}
        open={modalOpen}
        onClose={closeModal}
        intent={owner ? AuctionPermissionIntent.Start : AuctionPermissionIntent.Bid}
        auctionId={owner ? undefined : id}
        cadastralPlotId={owner ? id : undefined}
      />
    </Container>
  ) : null
})
